import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Toggle from 'react-bootstrap-toggle';
import WebsocketService from '@upstart/harmony/lib/websocket-service';
import { EventType } from '@upstart/harmony/lib/types/websocketTypes';
import { Origin } from '@upstart/harmony/lib/constants';
import {
  getDatePickerRangeOptions,
  getDatePickerRangeLabel,
} from '../../util/utils';

import dealsActions from '../../redux/deals/actions';
import dealerActions from '../../redux/dealers/actions';
import DealList from '../../components/DealList';
import SidebarContainer from '../SidebarContainer';
import './ManagerDashboard.scss';
import useLocalStorage from '../../util/useLocalStorage';
import LoadingOverlay from '../../components/LoadingOverlay';
import { SuperFlatBanner } from '../Banners/SuperFlatBanner';

const THROTTLE_RECENT_DEAL_LIST_REQUEST_IN_MS = 5 * 1000; // 5 seconds;

function ManagerDashboard(props) {
  const { actions, deals, dealer, leadSource, view } = props;

  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf('day'),
    endDate: moment().endOf('day'),
  });

  const ranges = getDatePickerRangeOptions();

  const rangeLabel = useMemo(
    () =>
      getDatePickerRangeLabel(ranges, dateRange.startDate, dateRange.endDate),
    [dateRange, ranges],
  );

  const displayRange = useMemo(
    () =>
      [
        moment(dateRange.startDate).format('YYYY/MM/DD'),
        moment(dateRange.endDate).format('YYYY/MM/DD'),
      ].join(' - '),
    [dateRange],
  );

  useEffect(() => {
    const { startDate, endDate } = dateRange;
    if (dealer && dealer.id) {
      const throttledRecentDealListRequest = _.throttle(
        () =>
          actions.recentDealListRequest({
            dealer,
            startDate: moment(startDate).toISOString(),
            endDate: moment(endDate).toISOString(),
            leadSource,
          }),
        THROTTLE_RECENT_DEAL_LIST_REQUEST_IN_MS,
        { leading: true, trailing: true },
      );

      // Subscribe to any websocketEvents on deal create of update, and only then request a showroom list - replaces the polling we had before
      WebsocketService.subscribe(
        {
          dealerId: dealer.id,
          eventType: EventType.DEAL_CREATED_OR_UPDATED,
        },
        throttledRecentDealListRequest,
      );

      // Subscribe to any websocketEvents on deal create of update, and only then request a showroom list - replaces the polling we had before
      WebsocketService.subscribe(
        {
          dealerId: dealer.id,
          eventType: EventType.DEAL_PENCIL_UPDATED,
        },
        throttledRecentDealListRequest,
      );

      // fetch new list based on date range
      actions.recentDealListRequest({
        dealer,
        startDate: moment(startDate).toISOString(),
        endDate: moment(endDate).toISOString(),
        leadSource,
      });
    }
    return () =>
      WebsocketService.unsubscribe({
        dealerId: dealer.id,
        eventType: EventType.DEAL_CREATED_OR_UPDATED,
      });
  }, [dateRange, dealer, actions, leadSource]);

  const applyRange = (event, { startDate, endDate }) => {
    setDateRange({ startDate, endDate });
  };

  const [showHiddenDeals, setShowHiddenDeals] = useLocalStorage(
    'showHiddenDeals',
    true,
  );
  const [hiddenDeals, setHiddenDeals] = useLocalStorage('hiddenDeals', []);

  return (
    <SidebarContainer>
      {props.user?.id && dealer && (
        <SuperFlatBanner dealer={dealer} userId={props.user.id} />
      )}
      <div className="dashboard-table">
        <div className="dashboard-filters">
          <div>
            <span className="display-range-label-name">{rangeLabel}</span>
            <span className="display-range-label-date">{displayRange}</span>
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onApply={applyRange}
              ranges={ranges}
            >
              <button
                type="button"
                className="btn btn-primary margin-left margin-right"
              >
                <i className="glyphicon glyphicon-calendar" />
              </button>
            </DateRangePicker>
            <span className="display-range-label">{}</span>
            {leadSource !== Origin.ONLINE && (
              <a className="dashboard-new-deal" href="/pencil" target="_blank">
                <i className="new-deal-icon" /> Create New Deal
              </a>
            )}
          </div>
          <div className="dashboard-show-all">
            <label>
              Show All:
              <Toggle
                on="on"
                off="off"
                size="sm"
                active={showHiddenDeals}
                onClick={(value) => setShowHiddenDeals(value)}
              />
            </label>
          </div>
        </div>
        {deals.fetching && !deals?.recentDealList?.length ? (
          <LoadingOverlay active />
        ) : (
          <DealList
            dealer={dealer}
            list={deals.recentDealList}
            error={deals.error}
            fetching={deals.fetching}
            showHiddenDeals={showHiddenDeals}
            hiddenDeals={hiddenDeals}
            setHiddenDeals={setHiddenDeals}
            onlineOnly={leadSource === Origin.ONLINE}
            view={view}
          />
        )}
      </div>
    </SidebarContainer>
  );
}

ManagerDashboard.propTypes = {
  deals: PropTypes.object,
  dealer: PropTypes.object,
  dealers: PropTypes.object,
  actions: PropTypes.object,
  leadSource: PropTypes.string,
  view: PropTypes.string,
};

const mapStateToProps = ({ deals, user, dealers }) => ({
  deals,
  user: user.currentUser,
  dealer: user.currentUserDealer,
  dealers,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...dealsActions,
      ...dealerActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManagerDashboard);
